/**
 * Reads the deployment environment straight from the two globals that carry it -
 * `window.LubaUiAppConfig.APP_ENVIRONMENT` (set by `config-generator.sh` at container start, see
 * `src/app/config.js`) and `process.env.NODE_ENV` (set at build/test time).
 *
 * Deliberately imports neither `app/config` nor `app/app-utils`: `app/app-utils`'s `isDev()` is
 * defined in terms of this module (see `isKnownNonProductionEnvironment` below), so importing
 * either of them back here would create the cycle, not avoid one - this module has to stay the
 * base of that relationship.
 *
 * Every export here re-reads the globals on every call rather than caching them at module-load
 * time - not because `window.LubaUiAppConfig` might not be set yet (it already is, or is
 * definitively absent, by the time any of this app's own code runs: `public/index.html` loads
 * `/config.js` as a plain, render-blocking `<script>` in `<head>`, finished before the bundled app
 * scripts later in the page even start), but so tests can flip `window.LubaUiAppConfig`/
 * `process.env.NODE_ENV` between assertions without needing `jest.resetModules()`.
 */

// APP_ENVIRONMENT values that mean "this is the real production deployment" - i.e. no pill, and
// not a candidate for isKnownNonProductionEnvironment() either. Compared against the normalized
// (trimmed/unquoted/upper-cased) value, so the env var itself can be any case.
const PRODUCTION_LABELS = ['LIVE', 'PROD', 'PRODUCTION'];

// The closed list `isKnownNonProductionEnvironment` (below) checks against - deliberately NOT the
// same permissive rule `getEnvironmentLabel`/`isNonProductionEnvironment` use for the pill. A typo
// or unexpected value on a LIVE deployment (`PRD`, `LIEV`, `prod-ee`, ...) must not flip on
// `isDev()`'s debug logging there - it's fine (arguably correct) for it to still show a pill,
// which is purely cosmetic and local to the header.
const KNOWN_NON_PRODUCTION_LABELS = ['TEST', 'DEV', 'DEVELOPMENT', 'STAGING', 'QA', 'LOCAL'];

// The environment pill (`app/header/lockup.tsx`) is a small, fully-rounded sash - a long label
// would either overflow it or force it to grow past the point of "small". lis-paasla's own pill
// uses the same 8-character cap, and every label this project actually sets in practice (TEST,
// DEV) is well under it - STAGING (7) is the longest of the closed, "known" list above that isn't
// truncated by it; anything longer just gets cut off rather than wrapping or overflowing.
const MAX_LABEL_LENGTH = 8;

// `APP_ENVIRONMENT=TEST` set via `docker run --env-file` (rather than `-e`) can leave one matching
// pair of literal quote characters in the value verbatim (the shell would normally strip them, but
// an env-file's `KEY=VALUE` line isn't shell-parsed at all) - e.g. `APP_ENVIRONMENT="TEST"` ends up
// as the four-character string `"TEST"`, not `TEST`. Strips exactly one such pair, then trims
// again (a value like `" TEST "` - quotes with padding inside them - is unusual but handled the
// same way either order), and upper-cases. Returns '' for an unset/empty value.
function normalizeAppEnvironment() {
  const raw = window?.LubaUiAppConfig?.APP_ENVIRONMENT;
  if (typeof raw !== 'string') {
    return '';
  }

  let value = raw.trim();
  const first = value[0];
  const last = value[value.length - 1];
  if (value.length >= 2 && ((first === '"' && last === '"') || (first === "'" && last === "'"))) {
    value = value.slice(1, -1).trim();
  }

  return value.toUpperCase();
}

/**
 * The label to show for the current deployment, or `null` when nothing should be shown at all
 * (production, or - a bare `window.LubaUiAppConfig.APP_ENVIRONMENT` aside - a local production
 * build preview with no dev server and no explicit APP_ENVIRONMENT).
 *
 * Deliberately permissive, unlike `isKnownNonProductionEnvironment` below: ANY non-empty value
 * that isn't exactly LIVE/PROD/PRODUCTION shows a pill with that value, closed-list or not - an
 * unrecognized or misspelled label showing an (equally unrecognized-looking) pill is a visible,
 * harmless hint that something about the deployment's config is off, not a debug-logging risk.
 *
 * - `APP_ENVIRONMENT` set to a non-empty, non-production value (once normalized - see
 *   `normalizeAppEnvironment`): that value, capped at `MAX_LABEL_LENGTH`.
 * - `APP_ENVIRONMENT` unset/empty, but `NODE_ENV === 'development'` (i.e. `npm start`): `'DEV'`.
 * - Otherwise (unset/empty and not a local dev server, or explicitly LIVE/PROD/PRODUCTION): `null`.
 */
export function getEnvironmentLabel() {
  const normalized = normalizeAppEnvironment();

  if (normalized) {
    if (PRODUCTION_LABELS.includes(normalized)) {
      return null;
    }
    return normalized.slice(0, MAX_LABEL_LENGTH);
  }

  if (isLocalDev()) {
    return 'DEV';
  }

  return null;
}

/** Whether the app is running anywhere other than the real production deployment. */
export function isNonProductionEnvironment() {
  return getEnvironmentLabel() !== null;
}

/**
 * Whether APP_ENVIRONMENT (normalized - see `normalizeAppEnvironment`) is one of a closed list of
 * recognized non-production names, regardless of `MAX_LABEL_LENGTH` (checked against the full
 * normalized value, not the truncated pill label - `DEVELOPMENT` is 11 characters, longer than the
 * 8-character cap, and must still match here). This is what `app/app-utils.js`'s `isDev()` is
 * actually built on: an env var typo or rename on a real LIVE deployment (`PRD`, `LIEV`,
 * `prod-ee`, ...) must not silently turn on debug logging there, the way the permissive
 * `isNonProductionEnvironment` above would.
 */
export function isKnownNonProductionEnvironment() {
  return KNOWN_NON_PRODUCTION_LABELS.includes(normalizeAppEnvironment());
}

/** Whether this is the local `npm start` dev server (`react-scripts start`), specifically. */
export function isLocalDev() {
  return process.env.NODE_ENV === 'development';
}
